import { gql } from '@apollo/client';
import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { styled } from 'twin.macro';
import 'styled-components/macro';

import { Button } from 'play/atoms/button/Button';
import { useCustomParameters } from 'play/support/useCustomParameters';
import { useTrackAmplitudeEvent } from 'support/amplitude/amplitudeEvent';
import { addInvitePrefix, pathPrefixByMode } from 'support/location';
import { QueryParams } from 'support/QueryParams';
import { ExpiredScreenTrackFragment, Mode } from 'types/graphql';

gql`
  fragment ExpiredScreenTrack on Track {
    slug
    permalink
    challenges {
      permalink
    }
  }
`;

export interface ExpiredScreenProps {
  mode: Mode;
  track: ExpiredScreenTrackFragment;
  teamSlug: string;
}

export const ExpiredScreen = ({ mode, track, teamSlug }: ExpiredScreenProps) => {
  const { inviteID } = useParams<{ inviteID?: string }>();

  const [customParameters] = useCustomParameters();
  const trackAmplitudeEvent = useTrackAmplitudeEvent();

  const closeToPath = inviteID ? `/${teamSlug}/invite/${inviteID}` : track?.permalink;

  useEffect(() => {
    trackAmplitudeEvent({ name: 'Lab - Session expired' });
  }, [trackAmplitudeEvent]);

  return (
    <Screen tw="flex items-center justify-center h-full w-full">
      <div tw="max-w-lg text-white text-center">
        <h1 tw="text-4xl font-medium">Your session expired</h1>
        <p tw="text-2xl leading-snug">If you want to try again, please restart the track.</p>
        <br />

        <div tw="text-base space-x-2">
          <Link
            to={
              mode === Mode.Embed
                ? `${pathPrefixByMode(mode)}/${teamSlug}/tracks/${track.slug}?${new URLSearchParams(
                    customParameters || '',
                  ).toString()}`
                : addInvitePrefix(`${track.challenges[0].permalink}/notes?${QueryParams.AUTO_START}=true`, inviteID)
            }
          >
            <Button>Restart track</Button>
          </Link>

          {mode === Mode.Platform && closeToPath && (
            <Link to={closeToPath} tw="text-gray-15 hover:underline">
              <Button variant="outline">Exit</Button>
            </Link>
          )}
        </div>
      </div>
    </Screen>
  );
};

const Screen = styled.div`
  background-color: ${props => props.theme.colors.blue.dark};
`;
